import {Injectable} from '@angular/core';
import {HttpInterceptorService} from '../../core/services/http-interceptor.service';
import {Observable} from 'rxjs/Observable';
import {EXPORT_DATA_STORAGE_KEY} from '@ispm/shared/services/ui-customization.service';
import {ExportDropdownOptions} from "../../worklist/interfaces/export.config.interface";
import {DynamicApiService} from "./dynamic-api.service";
import {NOTIFICATION_MESSAGE} from "@ispm-client/modules/aggregator/services/consts";
import {ActivityService} from '@ispm/user/services/activity.service';
import {set, get} from "lodash";
import {ActivityActions} from "@ispm/user/constants/activityActions";
import {NotificationMessageTypes} from "@ispm/core/constants/notification-message-types.constant";
import {map, take} from "rxjs/operators";
import {BehaviorSubject, Subject} from "rxjs";
import {IspmHttpClient} from "@ispm/core/services/ispm-http-client";
import {ExportNotificationService} from "./export-notification-service";
import {ExportOptionStatusUpdate} from "../classes/export-options";
import {FilterService} from "../../filter/services/filter.service";
import {ExportType} from "../constants/export-types.constant";
import {CoreActionCreatorService} from "@ispm/core/services/core-action-creator.service";

@Injectable()
export class ExportWorklistDataService {
  static readonly baseUrl = '/report';
  static readonly EXPORT_DATA_CONFIGURATION_TEMPLATE = 'DEFAULT_WEB_CONFIGURATION';

  private worklistType: string = '';
  private worklistAlias: string;
  private worklistTemplate: string;
  public startPollingSubject: Subject<void> = new Subject<void>();
  public exportDataOptionStatusUpdateSubject: BehaviorSubject<ExportOptionStatusUpdate> = new BehaviorSubject({disabled:false});
  private _exportDataConfiguration: any;

  constructor(private exportNotificationService: ExportNotificationService,
              private notificationMessage: NOTIFICATION_MESSAGE,
              private filterService: FilterService,
              private http: HttpInterceptorService,
              private dynamicApiService: DynamicApiService,
              private activityService: ActivityService,
              private ispmHttpClient: IspmHttpClient,
              private coreActions: CoreActionCreatorService) {
  }

  private setWorklistType(worklistType: string) {
    this.worklistType = worklistType;
  }

  /**
   * @description Gives ability to have custom name for the exported excel file
   * Helpful when the exportType is "generic"
   * @param worklistAlias
   */
  private setWorklistAlias(worklistAlias: string) {
    this.worklistAlias = worklistAlias;
  }

  public getExportWorklistDataOptions(): Array<any> {
    const options: any[] = [{
      label: 'All Fields',
      command: () => {
        console.log('Command: Export data for all fields');
        this.exportAllFieldsDataToExcel('All Fields');
      }
    }, {
      label: 'Current Fields',
      command: () => {
        console.log('Command: Export data for current fields');
        this.exportCurrentFieldsDataToExcel('Current Fields');
      }
    }];

    return options;
  }

  public setExportWorklistDataOptionDisabledStatus(exportOptionStatusUpdate:ExportOptionStatusUpdate, worklistTemplate: string, isAll: boolean = false) {
    if (this.worklistTemplate && (this.worklistTemplate === worklistTemplate || isAll))
      this.exportDataOptionStatusUpdateSubject.next(exportOptionStatusUpdate);
  }

  public setCurrentWorklistTemplate(template) {
    this.worklistTemplate = template;
    this.dynamicApiService.setCurrentWorklistTemplate(template);
  }

  private exportCurrentFieldsDataToExcel(selectedOptionLabel: string) {
    this.getExcelDataFromExportData('currentFields', selectedOptionLabel);
  }

  private exportAllFieldsDataToExcel(selectedOptionLabel: string) {
    this.getExcelDataFromExportData('allFields', selectedOptionLabel);
  }

  private getExcelDataFromExportData(selectedOption: string, selectedOptionLabel: string) {
    const exportDataStr = sessionStorage.getItem(EXPORT_DATA_STORAGE_KEY);
    if (exportDataStr) {
      const exportData = JSON.parse(exportDataStr);
      this.setExportWorklistDataOptionDisabledStatus({disabled:true,exportRunning:true}, exportData['worklistTemplateId']);
      if (!!exportData.selectionCount && !!exportData.maxExportedItems && exportData.selectionCount > exportData.maxExportedItems) {
        this.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, exportData['worklistTemplateId']);
        this.exportNotificationService.setNotificationMessage(NotificationMessageTypes.ERROR, 'Total selected items for export to excel exceeds the maximum of ' + exportData.maxExportedItems + ' items');
        return;
      }

      if (exportData.hasOwnProperty(selectedOption)) {
        if (exportData.exportMethodAsync) {
          //Check if open export data activity for the current worklist and the user exists
          this.getExportDataActiveTasks(exportData.performer, exportData['worklistTemplateId'])
            .pipe(take(1))
            .subscribe((activities) => {
              if (activities && activities.length > 0) {
                this.exportNotificationService.setNotificationMessage(NotificationMessageTypes.WARN, "The request for exporting data from the current worklist is already processing.");
              } else {
                //Create an empty export data activity for the user
                this.activityService.createTaskActivity(exportData.performer, '', 'ExportDataToExcelTask', [
                  {
                    "reference": exportData['worklistTemplateId'],
                    "resourceType": "WorklistTemplate"
                  }])
                  .subscribe((response) => {
                    let activity = response.json();
                    this.sendExportDataToExcel(exportData, selectedOption, activity);
                  });
              }
            });
        } else {
          this.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, exportData['worklistTemplateId']);
          this.sendExportDataToExcel(exportData, selectedOption);
        }
      } else {
        this.exportNotificationService.setNotificationMessage(NotificationMessageTypes.WARN, `${selectedOptionLabel}  ${this.notificationMessage.IS_MISSING_IN_CONFIGURATION}`);
        this.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, exportData['worklistTemplateId']);
      }
    } else {
      this.exportNotificationService.setNotificationMessage(NotificationMessageTypes.WARN, this.notificationMessage.NO_ITEMS_TO_EXPORT);
    }
  }

  private sendExportDataToExcel(exportData: any, selectedOption: string, activity: any = null) {
    this.sendDataToExcel(exportData, selectedOption)
      .take(1)
      .subscribe((result) => {
        if (!exportData.exportMethodAsync) {
          //result = excel Base64 data
          if (result && Object.keys(result).length > 0) {
            this.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, exportData['worklistTemplateId']);
            this.displayExcel(result);
          }
        } else {
          //result = export data task ID
          if (result && Object.keys(result).length > 0) {
            let taskId = result.body?.taskId;
            this.exportNotificationService.setNotificationMessage(NotificationMessageTypes.INFO, this.notificationMessage.ASYNC_EXPORT_DATA_TO_EXCEL_STARTED, null, 5000);
            if (activity && activity.context && activity.context.length > 0) {
              //Set the related task ID to an empty export data activity for the user
              this.activityService.updateActivityContext(activity, taskId)
                .catch((err) => {
                  console.error(`sendExportDataToExcel :: activity update error => activity: ${JSON.stringify(activity)} - error: ${err}"`);
                })
                .finally(() => {
                  this.startPollingSubject.next();
                });
            }
          } else {
            this.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, exportData['worklistTemplateId']);
            if (activity) {
              this.activityService.updateActivityAction(activity, exportData.performer, ActivityActions.FAILED);
            }
          }
        }
      });
  }

  private sendDataToExcel = (exportData: any, selectedOption: string): Observable<any> => {
    if (exportData && exportData.hasOwnProperty(selectedOption)) {
      exportData[selectedOption].timeZoneId = this.getTimezoneId();
      exportData[selectedOption].performer = exportData.performer;
      exportData[selectedOption].worklistType = exportData['worklistType'];
      exportData[selectedOption].worklistAlias = exportData['worklistAlias'];
      exportData[selectedOption].worklistTemplate = {
        resourceType: 'WorklistTemplate',
        id: exportData['worklistTemplateId']
      };

      if (exportData.hasOwnProperty('definition')) {
        exportData[selectedOption].definition = {} = exportData.definition;
      }
      if (exportData.hasOwnProperty('baseWorklistTemplateId')) {
        exportData[selectedOption].baseWorklistTemplateId = exportData['baseWorklistTemplateId'];
      }

      exportData[selectedOption].exportSelectedOption = selectedOption;
      if (exportData[selectedOption].hasOwnProperty('currentFields')
        && exportData[selectedOption].currentFields.hasOwnProperty('resources')) {
        exportData[selectedOption].currentFields.resources.forEach(resource => {
          if (resource.hasOwnProperty('ids')) {
            console.log('Exporting data for ' + resource.ids.length + 'ids.');
          }
        });
      }
      if (exportData[selectedOption].hasOwnProperty('allFields')
        && exportData[selectedOption].allFields.hasOwnProperty('resources')) {
        exportData[selectedOption].allFields.resources.forEach(resource => {
          if (resource.hasOwnProperty('ids')) {
            console.log('Exporting data for ' + resource.ids.length + 'ids.');
          }
        });
      }

      this.setWorklistType(exportData['worklistType']);
      this.setWorklistAlias(exportData['worklistAlias']);

      const exportDataSelection = {
        exportData: exportData[selectedOption],
        query: exportData.query
      };

      exportDataSelection.exportData['exportMethodAsync'] = exportData['exportMethodAsync'];
      exportDataSelection.exportData['taskType'] = "ExportWorklistDataToExcel";

      const req = this.http.post(ExportWorklistDataService.baseUrl + '/exportDataToExcel', exportDataSelection, true)
        .map(data => {
          return exportData.exportMethodAsync ? data : data.json();
        })
        .catch(err => {
          this.exportNotificationService.setNotificationMessage(NotificationMessageTypes.ERROR, this.notificationMessage.FAILED_TO_EXPORT_DATA_TO_EXCEL);
          console.error(`error exporting data to excel : ${JSON.stringify(err)}`);
          return Observable.of({});
        }).publish();
      req.connect();
      return req;
    }
  }

  public displayExcel(excelData: any, fileName: string = null) {
    if (excelData) {
      const binaryExcelData = this.base64ToArrayBuffer(excelData.content);
      /* Let the user save the Excel file in local disk. */
      const blob = new Blob([binaryExcelData], {type: excelData.contentType});
      const objectURL = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      document.body.appendChild(link);
      // link.href = URL.createObjectURL(blob);
      link.href = objectURL;
      //  link.target = '_blank';
      link.download = fileName ? fileName : (this.worklistAlias ? this.worklistAlias + 'Exported.xlsx' : this.worklistType + 'WorklistExported.xlsx');
      link.click();
    }
  }

  public getExportDataActiveTasks(actorId, worklistTemplate: string = null, context: string = "ExportDataToExcelTask"): Observable<any> {
    return this.activityService.searchActivity(context, actorId, ActivityActions.CREATE)
      .pipe(
        take(1),
        map(activities => {
            let activitiesList: any[] = [];
            if (Array.isArray(activities)) {
              activities.forEach(activity => {
                //Update action for all expired activities to 'expired' and remove them from the list
                //Sample time used to differ the expired activities (that were sampled but the related task was not completed in the period of the expiration time)
                //from the activities that were not sampled due to the user inactivity (logged out)
                let activityExpirationTimeSec = this._exportDataConfiguration ? this._exportDataConfiguration.activityExpirationTimeSec : 360;
                let activityCreateTime = new Date(activity.meta.created);
                // let activitySampleTime = activity.sampleAt ? new Date(activity.sampleAt) : new Date();
                const diffCreate = (new Date().getTime() - activityCreateTime.getTime()) / 1000;

                /* GEN-206273 :
                  If the user is logging in for the first time, the default polling expiration time is assigned.
                  If an activity-specific time is defined in the activity context, the following logic will recheck activityExpirationTimeSec.*/
                if(activity.context.some(resource => resource.resourceType === 'activityExpirationTimeSec')){
                  const activityExpirationIndex = activity.context.findIndex( obj => obj.resourceType === 'activityExpirationTimeSec');
                  if(activityExpirationIndex>-1){
                    activityExpirationTimeSec = activity.context[activityExpirationIndex].reference;
                  }
                }

                // const diffSample = (new Date().getTime() - activitySampleTime.getTime()) / 1000;
                // if (diffCreate >= activityExpirationTimeSec && diffSample < 120) {
                if (diffCreate >= activityExpirationTimeSec ) {
                    //Mark the activity as EXPIRED, it will be updated in the DB by the further polling execution and the user will be notified
                    set(activity, 'action', ActivityActions.EXPIRED);
                }

                if (worklistTemplate && activity?.context && activity.context.length > 1) {
                  let activityWorklistTemplate = activity.context[1].reference;
                  activity = activityWorklistTemplate === worklistTemplate ? activity : null;
                }

                if (activity) {
                  activitiesList.push(activity);
                }
              });
            }
            return activitiesList;
          }
        )
      )
      .catch((err) => {
        console.error(`getExportDataActiveTasks :: getting open activities error => user ID: ${actorId} - error: ${err}", activity, err`);
        this.setExportWorklistDataOptionDisabledStatus({disabled:false,exportRunning:false}, null, true);
        return [];
      })
  }

  public getWorklistExportToExcelTaskData(taskId) {
    return this.http.get(ExportWorklistDataService.baseUrl + '/getExportDataToExcelTaskStatus', {taskId: taskId}, false).toPromise()
      .then((data) => {
        if (data)
          return data.json();
      })
      .catch((err) => {
        console.error(`getWorklistExportToExcelTaskData :: getting task data error => task ID: ${taskId} - error: ${err}"`);
        throw (err);
      });
  }



  public loadExportDataConfiguration(): Observable<any> {
    return this.ispmHttpClient.get('entity-tree/webconfiguration/' + ExportWorklistDataService.EXPORT_DATA_CONFIGURATION_TEMPLATE)
      .pipe(take(1))
      .map((data) => {
        this._exportDataConfiguration = data?.worklistExportDataConfiguration;
        return data?.worklistExportDataConfiguration;
      });
  }

  private base64ToArrayBuffer(base64Data): ArrayBuffer {
    const binaryStr = window.atob(base64Data);
    const len: number = binaryStr.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private getTimezoneId() {
    return new Date().toTimeString().split(' ')[1];
  }

  public createDataOptions(dropdownOptions: Partial<ExportDropdownOptions>[], chunkSize: number) {
    const totalOptions = [];
    dropdownOptions.forEach((options) => {
      totalOptions.push({
        label: options.label,
        command: this.generateCommand(options, chunkSize)
      });
    });
    return totalOptions;
  }

  private generateCommand(commandOptions: Partial<ExportDropdownOptions>, chunkSize: number) {
    switch (commandOptions.type) {
      case 'allFieldsExport':
        return () => {
          this.exportAllFieldsDataToExcel('All Fields');
        };
      case 'currentFieldsExport':
        return () => {
          this.exportCurrentFieldsDataToExcel('Current Fields');
        };
      case 'generic':
        if (commandOptions.ApiResource) {
          if(get(commandOptions, 'exportMethodAsync', false)) {
            const exportConfig = {
              exportingTaskType:commandOptions['exportMethodAsyncTaskType'],
              exportBasedOnActor:commandOptions['exportBasedOnActor'],
              exportType:commandOptions['exportType'],
              exportTitle:commandOptions['ApiResource']['exportTitle'],
              activityExpirationTimeSec:commandOptions['activityExpirationTimeSec']
            }
            return () => {
              set(commandOptions.ApiResource.payloadData, 'chunkSize', chunkSize);
              this.callAPIResource(commandOptions);
            }
          } else {
            return () => {
              set(commandOptions.ApiResource.payloadData, 'chunkSize', chunkSize);
              this.dynamicApiService.constructApiCall(commandOptions.ApiResource);
            };
          }

        }
        return null;
    }
  }

  private callAPIResource(commondOptions) {
    //Check if open export data activity for the current worklist and the user exists
    const exportDataStr = sessionStorage.getItem(EXPORT_DATA_STORAGE_KEY);
    let performer = null;
    if (exportDataStr) {
      const exportData = JSON.parse(exportDataStr);
      performer = exportData.performer;
    }
    else {
      this.exportNotificationService.setNotificationMessage(NotificationMessageTypes.WARN,this.notificationMessage.NO_ITEMS_SELECTED_FROM_WORKLIST);
      return;
    }
    const exportBasedOnActor = get(commondOptions, 'exportBasedOnActor', true);
    if(get(commondOptions, 'exportMethodAsync', false)) {
      this.coreActions.setLoader(true);
    }
    this.getExportDataActiveTasks(exportBasedOnActor ? performer: null, this.worklistTemplate, commondOptions['exportMethodAsyncTaskType'])
      .pipe(take(1))
      .subscribe((activities) => {
        if (activities && activities.length > 0) {
          this.setExportWorklistDataOptionDisabledStatus({disabled:true,exportRunning:true}, this.worklistTemplate, true);
          if(get(commondOptions, 'exportMethodAsync', false)) {
            this.coreActions.setLoader(false);
          }
          this.exportNotificationService.setNotificationMessage(NotificationMessageTypes.WARN, "The request for exporting data from the current worklist is already processing.");
        } else {
          //Create an empty export data activity for the user
          this.activityService.createTaskActivity(performer, '', commondOptions['exportMethodAsyncTaskType'], [
            {
              "reference": this.worklistTemplate,
              "resourceType": "WorklistTemplate"
            },{
              "reference": commondOptions['exportType'] !== undefined?commondOptions['exportType']:'ACR',
              "resourceType": "exportType"
            },{
              "reference": commondOptions['ApiResource']['exportTitle'] !== undefined?commondOptions['ApiResource']['exportTitle']:'lcsr_exam',
              "resourceType": "exportTitle"
            },{
              "reference": commondOptions['activityExpirationTimeSec'] !== undefined?commondOptions['activityExpirationTimeSec']:3600,
              "resourceType": "activityExpirationTimeSec"
            },{
              "reference": commondOptions['ApiResource']['maxExportLimit'],
              "resourceType": "maxExportLimit"
            }])
            .subscribe((activity) => {
                this.setExportWorklistDataOptionDisabledStatus({disabled:true,exportRunning:true}, this.worklistTemplate, true);
                this.dynamicApiService.constructApiCall(commondOptions.ApiResource,get(commondOptions, 'exportMethodAsync', false),activity.json(),this.startPollingSubject,this.exportDataOptionStatusUpdateSubject);
            });
        }
      });
  }

  public JSONToTextConvertor(JSONData, exportTitle){
    this.dynamicApiService.JSONToTextConvertor(JSONData,exportTitle)
  }

  public getAppliedFilterProperties(){
    return this.filterService.getAppliedFilterPropertiesFromStore();
  }

  public setCustomExpirationTime(expiration:any){
    if(expiration!==undefined && this._exportDataConfiguration !== undefined
      && this._exportDataConfiguration.activityExpirationTimeSec !== undefined
      && typeof expiration === 'number' && Number.isFinite(expiration)){
      this._exportDataConfiguration.activityExpirationTimeSec = expiration;
    }
  }
}
